import { SERVED_CONTRACT } from '../contracts/servedRecommendation.v1.js'

const ECONOMICS = SERVED_CONTRACT.served_economics
const EUR_TOLERANCE = Number(ECONOMICS.arithmetic_tolerance_eur)
const MARGIN_TOLERANCE = Number(ECONOMICS.margin_tolerance)

const ECONOMIC_NUMBER_FIELDS = Object.freeze([
  'pred_target_price',
  'price_amount_eur',
  'transport_cost_eur',
  'insurance_cost',
  'rdw_registration_cost',
  'other_costs',
  'customs_duty',
  'customs_btw',
  'customs_broker',
  'naks_cost',
  'customs_total',
  'cargo_insurance_cost',
  'import_costs_json_total',
  'import_fixed_costs',
  'bpm_rest',
  'potential_vat_liability',
  'expected_revenue',
  'expected_margin_pct',
  'vat_deductible_amount',
  'expected_revenue_excl_vat',
  'expected_margin_pct_excl_vat',
])

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function within(actual, expected, tolerance) {
  return Math.abs(actual - expected) <= tolerance
}

export function published80Interval(row) {
  const point = finiteNumber(row.pred_target_price)
  const lower = finiteNumber(row.pred_target_price_lower_80)
  const upper = finiteNumber(row.pred_target_price_upper_80)
  if (point == null || lower == null || upper == null) return null
  if (point <= 0 || lower < 0 || upper <= lower) return null
  if (lower > point || upper < point) return null

  return Object.freeze({
    point,
    lower,
    upper,
    widthPct: ((upper - lower) / point) * 100,
    downsidePct: ((point - lower) / point) * 100,
    upsidePct: ((upper - point) / point) * 100,
  })
}

export function servedEconomics(row) {
  const values = {}
  const issues = []

  for (const field of ECONOMIC_NUMBER_FIELDS) {
    const number = finiteNumber(row[field])
    if (number == null) issues.push(`missing_or_invalid:${field}`)
    else values[field] = number
  }

  for (const field of ['import_costs_calculated', 'is_vat_new']) {
    if (typeof row[field] !== 'boolean') issues.push(`missing_or_invalid:${field}`)
    else values[field] = row[field]
  }

  if (issues.length > 0) return Object.freeze({ valid: false, issues: Object.freeze(issues) })

  if (values.pred_target_price <= 0) issues.push('non_positive:pred_target_price')
  if (values.price_amount_eur <= 0) issues.push('non_positive:price_amount_eur')
  for (const field of ECONOMIC_NUMBER_FIELDS.filter(field => ![
    'pred_target_price',
    'price_amount_eur',
    'expected_revenue',
    'expected_margin_pct',
    'expected_revenue_excl_vat',
    'expected_margin_pct_excl_vat',
  ].includes(field))) {
    if (values[field] < 0) issues.push(`negative:${field}`)
  }

  const customsExpected = values.customs_duty + values.customs_btw +
    values.customs_broker + values.naks_cost
  if (!within(values.customs_total, customsExpected, EUR_TOLERANCE)) {
    issues.push('identity:customs_total')
  }

  const jsonTotalExpected = values.insurance_cost + values.rdw_registration_cost +
    values.other_costs + values.customs_total
  if (!within(values.import_costs_json_total, jsonTotalExpected, EUR_TOLERANCE)) {
    issues.push('identity:import_costs_json_total')
  }

  const fixedExpected = values.transport_cost_eur + values.import_costs_json_total
  if (!within(values.import_fixed_costs, fixedExpected, EUR_TOLERANCE)) {
    issues.push('identity:import_fixed_costs')
  }
  const displayedFixedExpected = values.transport_cost_eur + values.insurance_cost +
    values.rdw_registration_cost + values.other_costs + values.customs_total
  if (!within(values.import_fixed_costs, displayedFixedExpected, EUR_TOLERANCE)) {
    issues.push('identity:displayed_import_costs')
  }

  const totalCostsInclVat = values.price_amount_eur + values.import_fixed_costs +
    values.bpm_rest + values.potential_vat_liability
  const revenueExpected = values.pred_target_price - totalCostsInclVat
  if (!within(values.expected_revenue, revenueExpected, EUR_TOLERANCE)) {
    issues.push('identity:expected_revenue')
  }

  const deductibleExpected = values.potential_vat_liability + values.customs_btw
  if (!within(values.vat_deductible_amount, deductibleExpected, EUR_TOLERANCE)) {
    issues.push('identity:vat_deductible_amount')
  }

  const revenueExclExpected = values.expected_revenue + values.vat_deductible_amount
  if (!within(values.expected_revenue_excl_vat, revenueExclExpected, EUR_TOLERANCE)) {
    issues.push('identity:expected_revenue_excl_vat')
  }
  const displayedRevenueExclExpected = values.pred_target_price -
    (totalCostsInclVat - values.vat_deductible_amount)
  if (!within(values.expected_revenue_excl_vat, displayedRevenueExclExpected, EUR_TOLERANCE)) {
    issues.push('identity:displayed_expected_revenue_excl_vat')
  }

  const marginExpected = values.expected_revenue / values.pred_target_price
  if (!within(values.expected_margin_pct, marginExpected, MARGIN_TOLERANCE)) {
    issues.push('identity:expected_margin_pct')
  }

  const marginExclExpected = values.expected_revenue_excl_vat / values.pred_target_price
  if (!within(values.expected_margin_pct_excl_vat, marginExclExpected, MARGIN_TOLERANCE)) {
    issues.push('identity:expected_margin_pct_excl_vat')
  }

  if (issues.length > 0) return Object.freeze({ valid: false, issues: Object.freeze(issues) })

  return Object.freeze({
    valid: true,
    issues: Object.freeze([]),
    ...values,
    total_costs_incl_vat: totalCostsInclVat,
    total_costs_excl_vat: totalCostsInclVat - values.vat_deductible_amount,
    non_purchase_costs_incl_vat: totalCostsInclVat - values.price_amount_eur,
  })
}

export function servedTruthForDisplay(row) {
  const interval = published80Interval(row)
  const economics = servedEconomics(row)
  const truth = {
    // Keep the canonical fields as well as display aliases so the personal
    // planner can revalidate the same identities, including saved card rows.
    pred_target_price: finiteNumber(row.pred_target_price),
    pred_target_price_lower_80: interval?.lower ?? null,
    pred_target_price_upper_80: interval?.upper ?? null,
    economics_valid: economics.valid,
    economics_issues: economics.issues,
    sale_range_available: interval !== null,
    pred_sale_lo: interval?.lower ?? null,
    pred_sale_hi: interval?.upper ?? null,
    pred_sale_lo_validated: interval?.lower ?? null,
    pred_sale_hi_validated: interval?.upper ?? null,
    sale_range_width_pct: interval?.widthPct ?? null,
    sale_range_downside_pct: interval?.downsidePct ?? null,
    sale_range_upside_pct: interval?.upsidePct ?? null,
  }

  if (!economics.valid) {
    return Object.freeze({
      ...truth,
      estimated_sales_price_nl: finiteNumber(row.pred_target_price),
      price_it: finiteNumber(row.price_amount_eur),
      estimated_margin: null,
      margin_pct: null,
      import_cost_total: null,
    })
  }

  return Object.freeze({
    ...truth,
    estimated_sales_price_nl: economics.pred_target_price,
    price_it: economics.price_amount_eur,
    estimated_margin: economics.expected_revenue,
    margin_pct: economics.expected_margin_pct * 100,
    import_cost_total: economics.non_purchase_costs_incl_vat,
    total_costs_incl_vat: economics.total_costs_incl_vat,
    total_costs_excl_vat: economics.total_costs_excl_vat,
    bpm_cost: economics.bpm_rest,
    bpm_rest: economics.bpm_rest,
    import_fixed_costs: economics.import_fixed_costs,
    transport: economics.transport_cost_eur,
    transport_cost_eur: economics.transport_cost_eur,
    insurance: economics.insurance_cost,
    insurance_cost: economics.insurance_cost,
    rdw: economics.rdw_registration_cost,
    rdw_registration_cost: economics.rdw_registration_cost,
    other_costs: economics.other_costs,
    customs_duty: economics.customs_duty,
    customs_btw: economics.customs_btw,
    customs_broker: economics.customs_broker,
    naks_cost: economics.naks_cost,
    customs_total: economics.customs_total,
    cargo_insurance_cost: economics.cargo_insurance_cost,
    import_costs_json_total: economics.import_costs_json_total,
    import_costs_calculated: economics.import_costs_calculated,
    potential_vat_liability: economics.potential_vat_liability,
    is_vat_new: economics.is_vat_new,
    vat_deductible_amount: economics.vat_deductible_amount,
    expected_revenue: economics.expected_revenue,
    expected_margin_pct: economics.expected_margin_pct,
    expected_revenue_excl_vat: economics.expected_revenue_excl_vat,
    expected_margin_pct_excl_vat: economics.expected_margin_pct_excl_vat,
  })
}
